import { useState, useCallback, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import GoogleMap, { GoogleMapHandle } from '../maps/GoogleMap';
import GeofenceOverlay from '../maps/GeofenceOverlay';
import MapMarker from '../maps/MapMarker';
import { calculateZoomForRadius, parseCoordinate } from '../../utils/mapUtils';
import {
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  BellIcon,
  MagnifyingGlassIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';

interface Geofence {
  id: string;
  name: string;
  centerLatitude: number;
  centerLongitude: number;
  radiusMeters: number;
  fenceType: 'SAFE_ZONE' | 'RESTRICTED_ZONE' | 'ALERT_ZONE';
  isActive: boolean;
  alertOnEntry: boolean;
  alertOnExit: boolean;
}

interface ActivityMarker {
  id: string;
  title: string;
  activityType: string;
  status: string;
  locationLatitude: number;
  locationLongitude: number;
}

interface GeofenceManagerProps {
  apiKey: string;
  geofences: Geofence[];
  onGeofenceCreate: (geofence: Omit<Geofence, 'id'>) => void;
  onGeofenceUpdate: (id: string, geofence: Partial<Geofence>) => void;
  onGeofenceDelete: (id: string) => void;
  activities: ActivityMarker[];
  missingLocationCount: number;
}

// Module-level constant — stable reference across renders so GoogleMap's
// center/zoom useEffect doesn't fire on every re-render and override panToAndZoom
const DEFAULT_MAP_CENTER = { lat: 40.7128, lng: -74.0060 };
const DEFAULT_MAP_ZOOM = 12;

const getActivityStatusColor = (status: string): string => {
  switch (status) {
    case 'IN_PROGRESS': return '#16A34A'; // green-600
    case 'PLANNED':
    default: return '#2563EB'; // blue-600
  }
};

const getActivityIcon = (status: string): google.maps.Symbol => ({
  path: google.maps.SymbolPath.CIRCLE,
  fillColor: getActivityStatusColor(status),
  fillOpacity: 0.9,
  strokeColor: '#FFFFFF',
  strokeWeight: 2,
  scale: 8,
});

// Lightweight Places Autocomplete search input — integrates with the parent map
interface LocationSearchInputProps {
  onPlaceSelected: (lat: number, lng: number, name: string) => void;
  placeholder?: string;
}

const LocationSearchInput: React.FC<LocationSearchInputProps> = ({ onPlaceSelected, placeholder }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const onPlaceSelectedRef = useRef(onPlaceSelected);

  // Keep ref current — avoids stale closure in the place_changed listener
  useEffect(() => {
    onPlaceSelectedRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  useEffect(() => {
    if (!inputRef.current || autocompleteRef.current) {return;}
    autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
      types: ['geocode', 'establishment'],
      fields: ['formatted_address', 'geometry', 'name'],
    });
    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current?.getPlace();
      if (place?.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const name = place.formatted_address ?? place.name ?? '';
        onPlaceSelectedRef.current(lat, lng, name);
      }
    });
  }, []); // initialize once — listener reads latest callback via ref

  return (
    <div className="relative">
      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder ?? 'Search for a location...'}
        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 text-sm"
      />
    </div>
  );
};

const GeofenceManager: React.FC<GeofenceManagerProps> = ({
  apiKey,
  geofences,
  onGeofenceCreate,
  onGeofenceUpdate,
  onGeofenceDelete,
  activities,
  missingLocationCount,
}) => {
  // All hooks must be at the top level before any early returns
  const mapRef = useRef<GoogleMapHandle>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedGeofence, setSelectedGeofence] = useState<string | null>(null);
  const [newGeofence, setNewGeofence] = useState<Partial<Geofence>>({
    name: '',
    fenceType: 'SAFE_ZONE',
    radiusMeters: 100,
    alertOnEntry: false,
    alertOnExit: true,
    isActive: true,
  });
  const [editGeofence, setEditGeofence] = useState<Partial<Geofence>>({});

  // Refs so handleMapClick stays stable (avoids stale closure over isCreating/isEditing)
  const isCreatingRef = useRef(false);
  const isEditingRef = useRef(false);
  useEffect(() => { isCreatingRef.current = isCreating; }, [isCreating]);
  useEffect(() => { isEditingRef.current = isEditing; }, [isEditing]);

  const handleMapLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  }, []);

  // Auto-zoom to show all geofences when map loads or geofences change
  useEffect(() => {
    if (!map || geofences.length === 0) {
      return;
    }

    // Only auto-zoom when no geofence is selected
    if (selectedGeofence) {
      return;
    }

    // Filter valid geofences with coordinates
    const validGeofences = geofences.filter((geofence) => {
      const lat = parseCoordinate(geofence.centerLatitude);
      const lng = parseCoordinate(geofence.centerLongitude);
      return lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng);
    });

    if (validGeofences.length === 0) {
      return;
    }

    // Create bounds object
    const bounds = new google.maps.LatLngBounds();

    // Extend bounds for each geofence including its radius
    validGeofences.forEach((geofence) => {
      const lat = parseCoordinate(geofence.centerLatitude);
      const lng = parseCoordinate(geofence.centerLongitude);
      const radius = geofence.radiusMeters;

      // Calculate approximate bounds for the circle
      // 1 degree latitude ≈ 111,000 meters
      const latOffset = radius / 111000;
      const lngOffset = radius / (111000 * Math.cos((lat * Math.PI) / 180));

      // Extend bounds to include the entire geofence circle
      bounds.extend(new google.maps.LatLng(lat + latOffset, lng + lngOffset));
      bounds.extend(new google.maps.LatLng(lat - latOffset, lng - lngOffset));
      bounds.extend(new google.maps.LatLng(lat + latOffset, lng - lngOffset));
      bounds.extend(new google.maps.LatLng(lat - latOffset, lng + lngOffset));
    });

    // Fit the map to the bounds with padding
    map.fitBounds(bounds, {
      top: 50,
      right: 50,
      bottom: 50,
      left: 50,
    });

    // If only one geofence, set a reasonable zoom level based on radius
    if (validGeofences.length === 1) {
      google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        const zoom = calculateZoomForRadius(validGeofences[0].radiusMeters);
        map.setZoom(zoom);
      });
    }
  }, [map, geofences, selectedGeofence]);

  // Zoom to selected geofence
  useEffect(() => {
    if (selectedGeofence && mapRef.current) {
      const geofence = geofences.find(g => g.id === selectedGeofence);
      if (geofence) {
        const lat = parseCoordinate(geofence.centerLatitude);
        const lng = parseCoordinate(geofence.centerLongitude);
        const zoom = calculateZoomForRadius(geofence.radiusMeters);

        mapRef.current.panToAndZoom({ lat, lng }, zoom);
      }
    }
  }, [selectedGeofence, geofences]);

  // Stable click handler — reads current mode via refs, never recreated
  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (!event.latLng) {return;}
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    if (isCreatingRef.current) {
      setNewGeofence(prev => ({ ...prev, centerLatitude: lat, centerLongitude: lng }));
    } else if (isEditingRef.current) {
      setEditGeofence(prev => ({ ...prev, centerLatitude: lat, centerLongitude: lng }));
    }
  }, []);

  // Show fallback if no API key (after all hooks)
  if (!apiKey || apiKey === 'demo-key') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-warning-600">
            <BellIcon className="w-5 h-5 mr-2" />
            Geofence Manager
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-96 bg-secondary-100 dark:bg-secondary-800 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <BellIcon className="w-16 h-16 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                Google Maps API Key Required
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-4">
                Please add your Google Maps API key to .env.local to enable geofence management.
              </p>
              <code className="text-sm bg-secondary-200 dark:bg-secondary-700 px-2 py-1 rounded">
                VITE_GOOGLE_MAPS_API_KEY=your_key_here
              </code>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const startCreating = () => {
    setIsCreating(true);
    setIsEditing(false);
    setSelectedGeofence(null);
    setNewGeofence({
      name: '',
      fenceType: 'SAFE_ZONE',
      radiusMeters: 100,
      alertOnEntry: false,
      alertOnExit: true,
      isActive: true,
    });
  };

  const cancelCreating = () => {
    setIsCreating(false);
    setNewGeofence({});
  };

  const createGeofence = () => {
    if (newGeofence.name && newGeofence.centerLatitude && newGeofence.centerLongitude) {
      onGeofenceCreate(newGeofence as Omit<Geofence, 'id'>);
      cancelCreating();
    }
  };

  const startEditing = () => {
    const geofence = geofences.find(g => g.id === selectedGeofence);
    if (geofence) {
      setEditGeofence({ ...geofence });
      setIsEditing(true);
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditGeofence({});
  };

  const saveGeofence = () => {
    if (selectedGeofence && editGeofence.centerLatitude && editGeofence.centerLongitude) {
      onGeofenceUpdate(selectedGeofence, editGeofence);
      cancelEditing();
    }
  };

  const toggleGeofenceActive = (id: string, isActive: boolean) => {
    onGeofenceUpdate(id, { isActive });
  };

  const deleteGeofence = (id: string) => {
    // eslint-disable-next-line no-alert
    if (confirm('Are you sure you want to delete this geofence?')) {
      onGeofenceDelete(id);
      if (selectedGeofence === id) {
        setSelectedGeofence(null);
        setIsEditing(false);
      }
    }
  };

  const getGeofenceIcon = (type: string) => {
    switch (type) {
      case 'SAFE_ZONE':
        return <ShieldCheckIcon className="w-4 h-4 text-green-600" />;
      case 'RESTRICTED_ZONE':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />;
      case 'ALERT_ZONE':
        return <BellIcon className="w-4 h-4 text-yellow-600" />;
      default:
        return <BellIcon className="w-4 h-4 text-blue-600" />;
    }
  };

  const getGeofenceTypeColor = (type: string) => {
    switch (type) {
      case 'SAFE_ZONE':
        return 'text-green-600 bg-green-100 dark:bg-green-900/20';
      case 'RESTRICTED_ZONE':
        return 'text-red-600 bg-red-100 dark:bg-red-900/20';
      case 'ALERT_ZONE':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20';
      default:
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20';
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Geofence List */}
      <div className="lg:col-span-1">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Geofences ({geofences.length})</CardTitle>
              <Button
                size="sm"
                onClick={startCreating}
                disabled={isCreating}
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                Create
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {geofences.map((geofence) => (
              <div
                key={geofence.id}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedGeofence === geofence.id
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                onClick={() => {
                  setSelectedGeofence(geofence.id);
                  setIsEditing(false);
                  setIsCreating(false);
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {getGeofenceIcon(geofence.fenceType)}
                    <h4 className="font-medium text-sm">{geofence.name}</h4>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGeofenceActive(geofence.id, !geofence.isActive);
                      }}
                      className={`w-3 h-3 rounded-full ${geofence.isActive ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      title={geofence.isActive ? 'Active' : 'Inactive'}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <div className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getGeofenceTypeColor(geofence.fenceType)}`}>
                    {geofence.fenceType.replace('_', ' ')}
                  </div>
                  <p className="text-xs text-gray-500">
                    Radius: {geofence.radiusMeters}m
                  </p>
                  <div className="flex space-x-2 text-xs">
                    {geofence.alertOnEntry && (
                      <span className="text-blue-600">Entry Alert</span>
                    )}
                    {geofence.alertOnExit && (
                      <span className="text-orange-600">Exit Alert</span>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {geofences.length === 0 && (
              <div className="text-center py-6 text-gray-500">
                <BellIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No geofences created yet</p>
                <p className="text-xs">Click &quot;Create&quot; to add your first geofence</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Map */}
      <div className="lg:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle>Geofence Map</CardTitle>
            {isCreating && (
              <p className="text-sm text-blue-600">
                Search for a location or click on the map to place your new geofence
              </p>
            )}
            {isEditing && (
              <p className="text-sm text-blue-600">
                Search for a location or click on the map to move the geofence center
              </p>
            )}
          </CardHeader>
          <CardContent>
            <div className="h-96 rounded-lg overflow-hidden relative">
              {/* Warning badge for activities missing location */}
              {missingLocationCount > 0 && (
                <div className="absolute top-3 right-3 z-10 flex items-center space-x-1 bg-amber-100 border border-amber-300 text-amber-800 text-xs font-medium px-2.5 py-1.5 rounded-full shadow-sm">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                  <span>{missingLocationCount} {missingLocationCount === 1 ? 'activity' : 'activities'} missing location</span>
                </div>
              )}
              <GoogleMap
                ref={mapRef}
                apiKey={apiKey}
                center={DEFAULT_MAP_CENTER}
                zoom={DEFAULT_MAP_ZOOM}
                onMapLoad={handleMapLoad}
                onClick={handleMapClick}
                className="w-full h-full"
              >
                {map && (
                  <>
                    {/* Existing geofences */}
                    {geofences.map((geofence) => (
                      <GeofenceOverlay
                        key={geofence.id}
                        map={map}
                        center={{
                          lat: geofence.centerLatitude,
                          lng: geofence.centerLongitude,
                        }}
                        radius={geofence.radiusMeters}
                        type={geofence.fenceType}
                        fillOpacity={geofence.isActive ? 0.5 : 0.25}
                        strokeOpacity={geofence.isActive ? 0.8 : 0.4}
                        onClick={() => {
                          setSelectedGeofence(geofence.id);
                          setIsEditing(false);
                          setIsCreating(false);
                        }}
                      />
                    ))}

                    {/* New geofence preview */}
                    {isCreating && newGeofence.centerLatitude && newGeofence.centerLongitude && (
                      <GeofenceOverlay
                        map={map}
                        center={{
                          lat: newGeofence.centerLatitude,
                          lng: newGeofence.centerLongitude,
                        }}
                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                        radius={newGeofence.radiusMeters || 100}
                        type={newGeofence.fenceType}
                        fillOpacity={0.5}
                        strokeOpacity={1}
                        strokeWeight={3}
                      />
                    )}

                    {/* Edit geofence preview */}
                    {isEditing && editGeofence.centerLatitude && editGeofence.centerLongitude && (
                      <GeofenceOverlay
                        map={map}
                        center={{
                          lat: editGeofence.centerLatitude,
                          lng: editGeofence.centerLongitude,
                        }}
                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                        radius={editGeofence.radiusMeters || 100}
                        type={editGeofence.fenceType}
                        fillOpacity={0.5}
                        strokeOpacity={1}
                        strokeWeight={3}
                      />
                    )}

                    {/* Activity markers */}
                    {activities.map((activity) => (
                      <MapMarker
                        key={activity.id}
                        map={map}
                        position={{ lat: activity.locationLatitude, lng: activity.locationLongitude }}
                        title={activity.title}
                        icon={getActivityIcon(activity.status)}
                      >
                        <div className="p-3 min-w-[200px]">
                          <h4 className="font-medium text-gray-900 dark:text-white">{activity.title}</h4>
                          <div className="mt-2 space-y-1">
                            <p className="text-xs text-gray-500">
                              Type: <span className="font-medium">{activity.activityType}</span>
                            </p>
                            <p className="text-xs" style={{ color: getActivityStatusColor(activity.status) }}>
                              {activity.status.replace('_', ' ')}
                            </p>
                          </div>
                        </div>
                      </MapMarker>
                    ))}
                  </>
                )}
              </GoogleMap>
            </div>
          </CardContent>
        </Card>

        {/* Geofence Details/Creation Form — below the map */}
        {(isCreating || selectedGeofence) && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>
                {isCreating ? 'Create Geofence' : isEditing ? 'Edit Geofence' : 'Geofence Details'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isCreating ? (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Name</label>
                    <input
                      type="text"
                      value={newGeofence.name ?? ''}
                      onChange={(e) => setNewGeofence(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                      placeholder="Enter geofence name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Type</label>
                    <select
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      value={newGeofence.fenceType || 'SAFE_ZONE'}
                      onChange={(e) => setNewGeofence(prev => ({ ...prev, fenceType: e.target.value as Geofence['fenceType'] }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                    >
                      <option value="SAFE_ZONE">Safe Zone</option>
                      <option value="RESTRICTED_ZONE">Restricted Zone</option>
                      <option value="ALERT_ZONE">Alert Zone</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Radius (meters)</label>
                    <input
                      type="number"
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      value={newGeofence.radiusMeters || 100}
                      onChange={(e) => setNewGeofence(prev => ({ ...prev, radiusMeters: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                      min="10"
                      max="10000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Location</label>
                    <LocationSearchInput
                      onPlaceSelected={(lat, lng) => {
                        setNewGeofence(prev => ({ ...prev, centerLatitude: lat, centerLongitude: lng }));
                        mapRef.current?.panToAndZoom({ lat, lng }, 15);
                      }}
                      placeholder="Search for a location..."
                    />
                  </div>

                  {newGeofence.centerLatitude && newGeofence.centerLongitude && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <p>📍 {newGeofence.centerLatitude.toFixed(6)}, {newGeofence.centerLongitude.toFixed(6)}</p>
                    </div>
                  )}

                  {!newGeofence.centerLatitude && (
                    <p className="text-sm text-blue-600">
                      Search for a location above or click on the map to set the geofence center
                    </p>
                  )}

                  <div className="flex space-x-2">
                    <Button
                      onClick={createGeofence}
                      disabled={!newGeofence.name || !newGeofence.centerLatitude}
                      className="flex-1"
                    >
                      Create Geofence
                    </Button>
                    <Button variant="outline" onClick={cancelCreating}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : isEditing ? (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Name</label>
                    <input
                      type="text"
                      value={editGeofence.name ?? ''}
                      onChange={(e) => setEditGeofence(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Type</label>
                    <select
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      value={editGeofence.fenceType || 'SAFE_ZONE'}
                      onChange={(e) => setEditGeofence(prev => ({ ...prev, fenceType: e.target.value as Geofence['fenceType'] }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                    >
                      <option value="SAFE_ZONE">Safe Zone</option>
                      <option value="RESTRICTED_ZONE">Restricted Zone</option>
                      <option value="ALERT_ZONE">Alert Zone</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Radius (meters)</label>
                    <input
                      type="number"
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      value={editGeofence.radiusMeters || 100}
                      onChange={(e) => setEditGeofence(prev => ({ ...prev, radiusMeters: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
                      min="10"
                      max="10000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Location</label>
                    <LocationSearchInput
                      onPlaceSelected={(lat, lng) => {
                        setEditGeofence(prev => ({ ...prev, centerLatitude: lat, centerLongitude: lng }));
                        mapRef.current?.panToAndZoom({ lat, lng }, 15);
                      }}
                      placeholder="Search to change location..."
                    />
                  </div>

                  {editGeofence.centerLatitude && editGeofence.centerLongitude && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <p>📍 {editGeofence.centerLatitude.toFixed(6)}, {editGeofence.centerLongitude.toFixed(6)}</p>
                      <p className="text-xs mt-1 text-blue-600">Or click on the map to move the center</p>
                    </div>
                  )}

                  <div className="flex space-x-2">
                    <Button
                      onClick={saveGeofence}
                      disabled={!editGeofence.name || !editGeofence.centerLatitude}
                      className="flex-1"
                    >
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={cancelEditing}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                selectedGeofence && (() => {
                  const geofence = geofences.find(g => g.id === selectedGeofence);
                  if (!geofence) { return null; }

                  return (
                    <>
                      <div className="space-y-2">
                        <h3 className="font-medium">{geofence.name}</h3>
                        <div className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getGeofenceTypeColor(geofence.fenceType)}`}>
                          {geofence.fenceType.replace('_', ' ')}
                        </div>
                        <p className="text-sm text-gray-600">
                          Radius: {geofence.radiusMeters} meters
                        </p>
                        <p className="text-sm text-gray-600">
                          Location: {geofence.centerLatitude.toFixed(6)}, {geofence.centerLongitude.toFixed(6)}
                        </p>
                        <div className="space-y-1">
                          {geofence.alertOnEntry && (
                            <p className="text-sm text-blue-600">✓ Entry alerts enabled</p>
                          )}
                          {geofence.alertOnExit && (
                            <p className="text-sm text-orange-600">✓ Exit alerts enabled</p>
                          )}
                        </div>
                        <p className={`text-sm ${geofence.isActive ? 'text-green-600' : 'text-gray-500'}`}>
                          Status: {geofence.isActive ? 'Active' : 'Inactive'}
                        </p>
                      </div>

                      <div className="flex space-x-2 flex-wrap gap-y-2">
                        <Button
                          size="sm"
                          onClick={startEditing}
                        >
                          <PencilIcon className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleGeofenceActive(geofence.id, !geofence.isActive)}
                        >
                          {geofence.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteGeofence(geofence.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </>
                  );
                })()
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default GeofenceManager;
